import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

export default function Admin() {
  const navigate = useNavigate();
  const [funders, setFunders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toggling, setToggling] = useState(null); // template id being toggled

  function handleLogout() {
    localStorage.removeItem("isLoggedIn");
    navigate("/");
  }

  async function loadFunders() {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API_URL}/admin/funders`);
      setFunders(res.data);
    } catch (err) {
      setError("Could not load funder data. Make sure the backend is running.");
    } finally {
      setLoading(false);
    }
  }

  async function handleToggleTemplate(funderId, templateId) {
    setToggling(templateId);
    try {
      await axios.patch(`${API_URL}/funders/${funderId}/templates/${templateId}/toggle`);
      await loadFunders();
    } catch (err) {
      setError("Failed to toggle template.");
    } finally {
      setToggling(null);
    }
  }

  async function handleDownloadTemplate(funderId, templateId, templateName) {
    try {
      const response = await axios.get(`${API_URL}/funders/${funderId}/templates/${templateId}/download-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${templateName}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError("Failed to download template PDF.");
    }
  }

  useEffect(() => {
    loadFunders();
  }, []);

  const headerStyle = {
    background: "linear-gradient(135deg, #010F08 0%, #004D35 60%, #00C07A 100%)",
    padding: "0 32px",
    height: "64px",
    display: "grid",
    gridTemplateColumns: "1fr auto 1fr",
    alignItems: "center",
    boxShadow: "0 2px 12px rgba(0,192,122,0.25)",
  };

  const badgeStyle = (active) => ({
    display: "inline-block",
    padding: "2px 10px",
    borderRadius: "12px",
    fontSize: "11px",
    fontWeight: "700",
    backgroundColor: active ? "#dcfce7" : "#fee2e2",
    color: active ? "#166534" : "#991b1b",
  });

  const typeColors = {
    mca_agreement: "#dbeafe",
    btf_addendum: "#fecaca",
    early_payoff_addendum: "#fcd34d",
    weekly_payments_addendum: "#a7f3d0",
    multiple_merchants_addendum: "#e9d5ff",
    personal_guarantee: "#fef9c3",
    security_agreement: "#fce7f3",
    bank_authorization: "#f3e8ff",
  };

  const formatCondition = (cond) => {
    if (!cond) return "Always";
    const { field, op, value } = cond;
    if (op === "eq") return `${field} = ${value}`;
    if (op === "truthy") return `${field} is set`;
    if (op === "gt") return `${field} > ${value}`;
    return "—";
  };

  return (
    <div style={{ backgroundColor: "#f4faf7", minHeight: "100vh", fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>

      {/* Header */}
      <div style={headerStyle}>
        <div>
          <button
            onClick={() => navigate("/app")}
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "6px", padding: "7px 16px", fontSize: "13px", fontWeight: "600", color: "#fff", cursor: "pointer" }}
          >
            ← Back to App
          </button>
        </div>
        <h1 style={{ fontSize: "20px", fontWeight: "800", color: "#fff", margin: 0, letterSpacing: "-0.3px" }}>
          Admin — Template Manager
        </h1>
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={handleLogout}
            style={{ background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.35)", borderRadius: "6px", padding: "8px 18px", fontSize: "13px", fontWeight: "600", color: "#fff", cursor: "pointer" }}
          >
            Log Out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: "1000px", margin: "0 auto", padding: "32px 16px" }}>

        {/* Page title */}
        <div style={{ marginBottom: "24px" }}>
          <h2 style={{ fontSize: "22px", fontWeight: "800", color: "#004D35", margin: 0 }}>Funder Accounts &amp; Contract Templates</h2>
          <p style={{ color: "#6b7280", fontSize: "13px", marginTop: "6px" }}>
            Manage which contract templates are active for each funder. Templates marked Active will be
            included when generating contract documents.
          </p>
        </div>

        {error && (
          <div style={{ backgroundColor: "#fee2e2", border: "1px solid #f87171", borderRadius: "8px", padding: "12px 16px", marginBottom: "20px", color: "#b91c1c", fontSize: "14px" }}>
            {error}
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#6b7280" }}>Loading funder data...</div>
        ) : funders.length === 0 ? (
          <div style={{ textAlign: "center", padding: "60px", color: "#6b7280" }}>No funders found.</div>
        ) : (
          funders.map(funder => (
            <div
              key={funder.id}
              style={{ backgroundColor: "white", borderRadius: "10px", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", padding: "24px", marginBottom: "24px", border: "1px solid #e5e7eb", borderLeft: "4px solid #004D35" }}
            >
              {/* Funder header */}
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    <h3 style={{ fontSize: "17px", fontWeight: "800", color: "#111827", margin: 0 }}>
                      {funder.company_name}
                    </h3>
                    <span style={{ fontSize: "11px", fontWeight: "700", color: "#004D35", backgroundColor: "#f0fdf4", padding: "2px 8px", borderRadius: "10px", border: "1px solid #bbf7d0" }}>
                      {funder.abbreviation}
                    </span>
                    <span style={badgeStyle(funder.is_active)}>
                      {funder.is_active ? "Active" : "Inactive"}
                    </span>
                  </div>
                  <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "4px" }}>
                    ID: {funder.id} &nbsp;|&nbsp; {funder.address} &nbsp;|&nbsp; {funder.phone} &nbsp;|&nbsp; {funder.email}
                  </div>
                </div>
                <div style={{ fontSize: "12px", color: "#6b7280", textAlign: "right" }}>
                  {funder.templates.filter(t => t.is_active).length} / {funder.templates.length} templates active
                </div>
              </div>

              {/* Templates table */}
              {funder.templates.length === 0 ? (
                <div style={{ color: "#9ca3af", fontSize: "13px", fontStyle: "italic", padding: "12px 0" }}>
                  No templates configured for this funder yet.
                </div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                  <thead>
                    <tr style={{ backgroundColor: "#f9fafb", borderBottom: "1px solid #e5e7eb" }}>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: "700", color: "#374151", width: "15%" }}>Template Name</th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: "700", color: "#374151", width: "10%" }}>Type</th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: "700", color: "#374151", width: "10%" }}>Role</th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: "700", color: "#374151", width: "12%" }}>Condition</th>
                      <th style={{ textAlign: "left", padding: "8px 12px", fontWeight: "700", color: "#374151", width: "8%" }}>States</th>
                      <th style={{ textAlign: "center", padding: "8px 12px", fontWeight: "700", color: "#374151", width: "22%" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {funder.templates.map((tmpl, i) => (
                      <tr
                        key={tmpl.id}
                        style={{
                          borderBottom: "1px solid #f3f4f6",
                          backgroundColor: i % 2 === 0 ? "white" : "#fafafa",
                        }}
                      >
                        <td style={{ padding: "10px 12px", fontWeight: "600", color: "#111827" }}>
                          {tmpl.template_name}
                          <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "2px", fontWeight: "400" }}>
                            {tmpl.file_path}
                          </div>
                        </td>
                        <td style={{ padding: "10px 12px" }}>
                          <span style={{
                            display: "inline-block",
                            padding: "2px 8px",
                            borderRadius: "10px",
                            fontSize: "11px",
                            fontWeight: "600",
                            backgroundColor: typeColors[tmpl.template_type] || "#f3f4f6",
                            color: "#374151",
                          }}>
                            {tmpl.template_type}
                          </span>
                        </td>
                        <td style={{ padding: "10px 12px", color: "#374151", fontSize: "12px" }}>
                          {tmpl.template_role ? (
                            <span style={{
                              display: "inline-block",
                              padding: "2px 6px",
                              borderRadius: "6px",
                              backgroundColor: "#ede9fe",
                              color: "#6b21a8",
                              fontSize: "11px",
                            }}>
                              {tmpl.template_role}
                            </span>
                          ) : "—"}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#6b7280", fontSize: "11px" }}>
                          {formatCondition(tmpl.inclusion_condition)}
                        </td>
                        <td style={{ padding: "10px 12px", color: "#374151", fontSize: "12px" }}>
                          {(tmpl.state_applicability || ["ALL"]).join(", ")}
                        </td>
                        <td style={{ padding: "10px 12px", textAlign: "center" }}>
                          <div style={{ display: "flex", gap: "6px", justifyContent: "center" }}>
                            {tmpl.file_format === "html" && (
                              <button
                                onClick={() => handleDownloadTemplate(funder.id, tmpl.id, tmpl.template_name)}
                                title="Download as PDF"
                                style={{
                                  padding: "4px 10px",
                                  borderRadius: "6px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: "pointer",
                                  border: "1px solid #d1d5db",
                                  backgroundColor: "#ffffff",
                                  color: "#374151",
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "4px",
                                }}
                              >
                                ⬇
                              </button>
                            )}
                            <button
                              onClick={() => handleToggleTemplate(funder.id, tmpl.id)}
                              disabled={toggling === tmpl.id}
                              style={{
                                padding: "4px 10px",
                                borderRadius: "6px",
                                fontSize: "12px",
                                fontWeight: "600",
                                cursor: toggling === tmpl.id ? "not-allowed" : "pointer",
                                border: "1px solid",
                                backgroundColor: tmpl.is_active ? "#dcfce7" : "#f3f4f6",
                                color: tmpl.is_active ? "#166534" : "#6b7280",
                                borderColor: tmpl.is_active ? "#86efac" : "#d1d5db",
                                opacity: toggling === tmpl.id ? 0.6 : 1,
                              }}
                            >
                              {toggling === tmpl.id ? "..." : tmpl.is_active ? "Active" : "Inactive"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          ))
        )}

        {/* Info panel */}
        <div style={{ backgroundColor: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: "8px", padding: "16px 20px", marginTop: "8px" }}>
          <div style={{ fontWeight: "700", color: "#004D35", fontSize: "13px", marginBottom: "6px" }}>Adding New Funders / Templates</div>
          <ul style={{ fontSize: "12px", color: "#374151", lineHeight: "1.8", paddingLeft: "16px", margin: 0 }}>
            <li>To add a new funder: add a record to the <code>funders</code> table in <code>Backend/mca_app.db</code> or extend <code>seed_initial_data()</code> in <code>database.py</code>.</li>
            <li>To add a new template: create the HTML file in <code>Backend/Templates/contracts/</code> and add a <code>contract_templates</code> record pointing to it.</li>
            <li>For DOCX or PDF-form templates: extend <code>contract_service.py</code> with the appropriate rendering path.</li>
            <li>State-specific templates: set <code>state_applicability</code> to e.g. <code>["NY", "CA"]</code> instead of <code>["ALL"]</code>.</li>
          </ul>
        </div>

      </div>
    </div>
  );
}
